import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const UpsellCrossSellChart = ({ data, teamName, currencySymbol = '' }) => {
  const svgRef = useRef();

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Clear previous chart
    d3.select(svgRef.current).selectAll('*').remove();

    // Set up dimensions
    const margin = { top: 40, right: 30, bottom: 60, left: 60 };
    const width = 900 - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    // Create SVG
    const svg = d3
      .select(svgRef.current)
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Parse months to get short month names
    const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const getMonthLabel = (monthStr) => {
      const parts = monthStr.split('-');
      if (parts.length === 2) {
        const monthNum = parseInt(parts[1]);
        return monthNames[monthNum] || monthStr;
      }
      return monthStr;
    };

    // X scale
    const xScale = d3
      .scalePoint()
      .domain(data.map(d => getMonthLabel(d.month)))
      .range([0, width])
      .padding(0.5);

    // Y scale - find max value for scale
    const maxValue = d3.max(data, d => Math.max(d.cumulativeActual, d.cumulativeTarget || 0));
    const yScale = d3
      .scaleLinear()
      .domain([0, maxValue * 1.1])
      .range([height, 0]);

    // Add X axis
    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('font-size', '11px');

    // Add Y axis with millions formatting
    svg
      .append('g')
      .call(
        d3.axisLeft(yScale)
          .ticks(6)
          .tickFormat(d => {
            // Convert to millions and format
            const millions = d / 1000000;
            return millions.toFixed(1);
          })
      )
      .selectAll('text')
      .style('font-size', '11px');

    // Add grid lines
    svg
      .append('g')
      .attr('class', 'grid')
      .attr('opacity', 0.1)
      .call(
        d3
          .axisLeft(yScale)
          .ticks(6)
          .tickSize(-width)
          .tickFormat('')
      );

    // Line generator for cumulative target
    const lineTarget = d3
      .line()
      .x(d => xScale(getMonthLabel(d.month)))
      .y(d => yScale(d.cumulativeTarget || 0))
      .curve(d3.curveMonotoneX);

    // Line generator for cumulative actual
    const lineActual = d3
      .line()
      .x(d => xScale(getMonthLabel(d.month)))
      .y(d => yScale(d.cumulativeActual))
      .curve(d3.curveMonotoneX);

    // Filter data to only include months with actual data available
    // Find the last month index that has actual data (actual is defined, including zero)
    let lastActualIndex = -1;
    for (let i = data.length - 1; i >= 0; i--) {
      // Check if actual is defined (not null/undefined) - zero is valid data
      if (data[i].actual != null) {
        lastActualIndex = i;
        break;
      }
    }
    const actualData = lastActualIndex >= 0 ? data.slice(0, lastActualIndex + 1) : [];

    // Add target line (if target data exists)
    if (data.some(d => d.cumulativeTarget > 0)) {
      svg
        .append('path')
        .datum(data)
        .attr('fill', 'none')
        .attr('stroke', '#bf9000')
        .attr('stroke-width', 2)
        .attr('d', lineTarget);

      // Add target dots
      svg
        .selectAll('.dot-target')
        .data(data)
        .enter()
        .append('circle')
        .attr('class', 'dot-target')
        .attr('cx', d => xScale(getMonthLabel(d.month)))
        .attr('cy', d => yScale(d.cumulativeTarget || 0))
        .attr('r', 4)
        .attr('fill', '#bf9000');

      // Add target value labels
      svg
        .selectAll('.label-target')
        .data(data.filter(d => d.cumulativeTarget > 0))
        .enter()
        .append('text')
        .attr('class', 'label-target')
        .attr('x', d => xScale(getMonthLabel(d.month)))
        .attr('y', d => {
          const targetY = yScale(d.cumulativeTarget || 0);
          const actualY = yScale(d.cumulativeActual);
          const diff = Math.abs(targetY - actualY);

          // If points are close, position target label below
          if (diff < 20) {
            return targetY + 15;
          } else if (d.cumulativeTarget > d.cumulativeActual) {
            // Target is higher, put label above
            return targetY - 10;
          } else {
            // Target is lower, put label below
            return targetY + 15;
          }
        })
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('font-weight', 'bold')
        .attr('fill', '#bf9000')
        .text(d => (d.cumulativeTarget / 1000000).toFixed(1));
    }

    // Add actual line (only for months with actual data)
    if (actualData.length > 0) {
      svg
        .append('path')
        .datum(actualData)
        .attr('fill', 'none')
        .attr('stroke', '#70ad47')
        .attr('stroke-width', 2)
        .attr('d', lineActual);
    }

    // Add actual dots (only for months with actual data)
    svg
      .selectAll('.dot-actual')
      .data(actualData)
      .enter()
      .append('circle')
      .attr('class', 'dot-actual')
      .attr('cx', d => xScale(getMonthLabel(d.month)))
      .attr('cy', d => yScale(d.cumulativeActual))
      .attr('r', 4)
      .attr('fill', '#70ad47');

    // Add actual value labels
    svg
      .selectAll('.label-actual')
      .data(actualData.filter(d => d.cumulativeActual > 0))
      .enter()
      .append('text')
      .attr('class', 'label-actual')
      .attr('x', d => xScale(getMonthLabel(d.month)))
      .attr('y', d => {
        const actualY = yScale(d.cumulativeActual);
        const targetY = yScale(d.cumulativeTarget || 0);
        const diff = Math.abs(actualY - targetY);

        // If points are close and target exists, position actual label above more
        if (d.cumulativeTarget > 0 && diff < 20) {
          return actualY - 18;
        } else {
          return actualY - 10;
        }
      })
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('font-weight', 'bold')
      .attr('fill', '#333')
      .text(d => (d.cumulativeActual / 1000000).toFixed(1));

    // Add chart title
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', -20)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', '600')
      .text(`Upsell Cross Sell tracker - ${teamName}`);

    // Add Y axis label
    svg
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -45)
      .attr('x', -height / 2)
      .attr('text-anchor', 'middle')
      .style('font-size', '11px')
      .text(`ACV (${currencySymbol}M)`);

  }, [data, teamName, currencySymbol]);

  return <svg ref={svgRef}></svg>;
};

export default UpsellCrossSellChart;
